/**
 * AML Sentinel Type Definitions
 *
 * Types for AML (Anti-Money Laundering) compliance features:
 * - Document forensic analysis
 * - Regulatory feed ingestion
 * - Transaction risk scoring
 * - Workflow management
 *
 * Note: Uses AML prefix for enums to distinguish from client-level types
 * (e.g., AMLRiskLevel for transactions vs RiskLevel for KYC)
 */

// =============================================================================
// Enums
// =============================================================================

/**
 * Risk level for AML transactions
 * Uses capitalized values (distinct from KYC RiskLevel which uses lowercase)
 */
export enum AMLRiskLevel {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
  CRITICAL = 'Critical',
}

/**
 * Status of an AML alert
 */
export enum AMLAlertStatus {
  NEW = 'New',
  INVESTIGATING = 'Investigating',
  CLEARED = 'Cleared',
  REPORTED = 'Reported',
}

/**
 * Role responsible for handling AML alerts
 */
export enum AMLAlertRole {
  FRONT_OFFICE = 'Front Office',
  COMPLIANCE = 'Compliance',
  LEGAL = 'Legal',
}

// =============================================================================
// Core Interfaces
// =============================================================================

/**
 * Monitoring Rule (extracted from regulations by AI)
 */
export interface MonitoringRule {
  id: string;
  ruleName: string;
  criteria: string;
  riskSeverity: AMLRiskLevel;
  sourceRegulationId?: string;
}

/**
 * Regulation (from RSS feed or manual upload)
 */
export interface Regulation {
  id: string;
  source: string;
  title: string;
  summary: string;
  rawContent: string;
  date: string;
  impactLevel: AMLRiskLevel;
  active: boolean;
  links: string[];
  extractedRules?: MonitoringRule[];
}

/**
 * AML Transaction for risk analysis
 */
export interface AMLTransaction {
  id: string;
  clientId?: string;
  clientName?: string;
  amount: number;
  currency: string;
  type: 'inbound' | 'outbound' | 'internal';
  counterparty: string;
  counterpartyCountry: string;
  date: string;
  reference?: string;
  riskScore?: number;
  riskLevel?: AMLRiskLevel;
  riskReasoning?: string;
  status: 'pending' | 'cleared' | 'flagged' | 'blocked';
}

/**
 * Alert note for audit trail
 */
export interface AlertNote {
  id: string;
  author: string;
  content: string;
  timestamp: string;
  action?: 'comment' | 'status_change' | 'assignment' | 'escalation';
}

/**
 * AML Alert generated from transaction monitoring
 */
export interface AMLAlert {
  id: string;
  transactionId: string;
  clientId?: string;
  clientName?: string;
  triggerReason: string;
  riskScore: number;
  riskLevel: AMLRiskLevel;
  status: AMLAlertStatus;
  assignedTo?: string;
  assignedRole: AMLAlertRole;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  notes: AlertNote[];
  workflowId?: string;
}

// =============================================================================
// Document Analysis
// =============================================================================

/**
 * Validation finding from document analysis
 */
export interface ValidationFinding {
  category: 'Formatting' | 'Spelling' | 'Tampering' | 'Authenticity';
  description: string;
  severity: AMLRiskLevel;
}

/**
 * Document analysis result from AI forensic analysis
 */
export interface DocumentAnalysisResult {
  fileName: string;
  uploadDate: string;
  riskScore: number;
  riskLevel: AMLRiskLevel;
  documentTypeDetected: string;
  findings: ValidationFinding[];
  summary: string;
  isStolenOrStock: boolean;
  onlineMatches: string[];
}

/**
 * Reverse image search result
 */
export interface ReverseSearchResult {
  hasMatches: boolean;
  urls: string[];
  fullMatches: string[];
  partialMatches: string[];
  visuallySimilar: string[];
  bestGuessLabels: string[];
}

// =============================================================================
// RSS Feeds
// =============================================================================

/**
 * RSS feed configuration for regulatory sources
 */
export interface RSSFeed {
  name: string;
  url: string;
  category: string;
}

// =============================================================================
// Workflows
// =============================================================================

/**
 * Single step in a remediation workflow
 */
export interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  assignedRole: AMLAlertRole;
  estimatedDuration: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  completedAt?: string;
  completedBy?: string;
}

/**
 * Workflow template for alert remediation
 */
export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  forRiskLevel: AMLRiskLevel;
  steps: WorkflowStep[];
  autoTrigger?: boolean;
  requiresApproval: boolean;
  approvalRole?: AMLAlertRole;
}

/**
 * Active workflow instance
 */
export interface WorkflowInstance {
  id: string;
  templateId: string;
  alertId: string;
  startedAt: string;
  startedBy: string;
  status: 'in_progress' | 'completed' | 'cancelled';
  currentStepIndex: number;
  steps: WorkflowStep[];
}

/**
 * Audit entry for compliance trail
 */
export interface AuditEntry {
  id: string;
  alertId: string;
  action: string;
  details: string;
  performedBy: string;
  timestamp: string;
  workflowStepId?: string;
}
